import type { SearchMembersFn } from '@/features/iam'
import { validationContext, required, func } from 'validx'
import { observable, action, computed } from 'mobx'
import { CursorPaged, type CursorPageQuery } from '@/infra/paging'
import { roundToPrecision } from '@/utils/number-util'
import { type User } from '@/features/iam/users/User'
import { type Id } from '@/infra/id'

/**
 * View model for a single allocation.
 */
export class AllocationInputViewModel {
  /**
   * The recipient (user getting paid).
   */
  @observable accessor recipient: User | null = null

  /**
   * The amount.
   */
  readonly allocatedAmount = new AmountEditor(() => this._getPayoutTotal())

  /**
   * The authorized partial payout amount.
   */
  readonly authorizedPartialPayoutAmount = new AmountEditor(
    () => this.allocatedAmount.value,
  )

  /**
   * Validation.
   */
  readonly validation = validationContext(this, {
    recipient: [required('Recipient is required')],
    allocatedAmount: [
      required('Amount is required'),
      func(
        () => this.allocatedAmount.value >= 0 || 'Amount must not be negative',
      ),
      func(
        () =>
          this.allocatedAmount.value <= this._getPayoutTotal() ||
          'Amount must not be greater than the total payout',
      ),
    ],
    authorizedPartialPayoutAmount: [
      required('Authorized partial payout amount is required'),
      func(
        () =>
          this.authorizedPartialPayoutAmount.value >= 0 ||
          'Amount must not be negative',
      ),
      func(
        () =>
          this.authorizedPartialPayoutAmount.value <=
            this.allocatedAmount.value ||
          'Authorized partial payout amount must not be greater than the allocated amount',
      ),
    ],
  })

  constructor(
    private readonly _workspaceId: Id,
    private readonly _getPayoutTotal: () => number,
    private readonly _findMembers: SearchMembersFn,
    private readonly _onRemove: () => void,
    public readonly isAlreadySelected: (user: User) => boolean,
  ) {}

  /**
   * Searches for members.
   */
  @action.bound
  async searchMembers(
    searchText: string,
    pagination: Partial<CursorPageQuery>,
    signal: AbortSignal,
  ) {
    const page = await this._findMembers(
      {
        searchText: searchText,
        after: pagination.after,
        limit: pagination.limit,
        workspaceId: this._workspaceId,
      },
      signal,
    )

    return CursorPaged.map(page, (d) => d.map((m) => m.user))
  }

  /**
   * Sets the recipient.
   */
  @action.bound
  setRecipient(recipient: User | null | undefined) {
    this.recipient = recipient ?? null
  }

  /**
   * Leaves out the users that other allocations already pay.
   */
  excludeAlreadySelected(users: readonly User[]) {
    return users.filter((user) => !this.isAlreadySelected(user))
  }

  /**
   * Asks to be removed from the list.
   */
  @action.bound
  remove() {
    this._onRemove()
  }

  /**
   * Validates the allocation.
   */
  @action.bound
  validate() {
    return this.validation.reset().validate().isValid
  }
}

/**
 * An editor for an amount that can be expressed as an absolute value
 * as well as a percentage value.
 */
export class AmountEditor {
  /**
   * The amount.
   */
  @observable accessor value = 0

  constructor(private readonly _getTotal: () => number) {}

  /**
   * The percentage.
   */
  @computed
  get percentage() {
    const total = this._getTotal()
    return roundToPrecision((this.value / total) * 100, 0)
  }

  /**
   * Sets the amount.
   */
  @action.bound
  setValue(amount: number) {
    this.value = amount
  }

  /**
   * Sets the percentage.
   */
  @action.bound
  setPercentage(percentage: number) {
    percentage = roundToPrecision(percentage, 0)

    this.value = roundToPrecision((this._getTotal() * percentage) / 100, 2)
  }
}
